import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  FormControl,
  FormLabel,
  Select,
  Spinner,
  Stack,
  Switch,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ReservedExecutionType,
  ReservedParticipationType,
  type SourcingEventV2Dto,
} from "../../../../../autogen/bff-api";
import { useSaveField } from "../hooks/useSaveField";
import {
  RESERVED_EXECUTION_OPTIONS,
  RESERVED_PARTICIPATION_OPTIONS,
} from "../utils/publicProcurementOptions";

interface Props {
  eventId: string;
  event: SourcingEventV2Dto;
}

const SettingsToggle = ({
  label,
  description,
  isChecked,
  isSaving: saving,
  onChange,
}: {
  label: string;
  description: string;
  isChecked: boolean;
  isSaving: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <Flex align="center" justify="space-between">
    <Box>
      <Text fontSize="sm" fontWeight="medium">
        {label} {saving && <Spinner size="xs" />}
      </Text>
      <Text fontSize="xs" color="smMuted">
        {description}
      </Text>
    </Box>
    <Switch
      colorScheme="teal"
      isChecked={isChecked}
      onChange={(e) => onChange(e.target.checked)}
    />
  </Flex>
);

export const AdvancedSettingsSection = ({ eventId, event }: Props) => {
  const { t } = useTranslation();
  const { saveField, isSaving } = useSaveField(eventId);

  const [euFunded, setEuFunded] = useState(event.publicContext.euFunded ?? false);
  const [gpaCoverage, setGpaCoverage] = useState(event.publicContext.gpaCoverage ?? false);
  const [reservedParticipation, setReservedParticipation] = useState<ReservedParticipationType>(
    event.publicContext.reservedParticipation ?? ReservedParticipationType.None
  );
  const [reservedExecution, setReservedExecution] = useState<ReservedExecutionType>(
    event.publicContext.reservedExecution ?? ReservedExecutionType.None
  );

  return (
    <Accordion allowToggle mb="8">
      <AccordionItem border="1px solid" borderColor="smBorder" borderRadius="lg">
        <AccordionButton py="3" borderRadius="lg">
          <Box flex="1" textAlign="left">
            <Text fontWeight="medium" fontSize="sm">
              {t("Advanced settings")}
            </Text>
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb="4">
          <Stack spacing="5">
            <SettingsToggle
              label={t("EU financing")}
              description={t("Is this procurement funded by the EU?")}
              isChecked={euFunded}
              isSaving={isSaving("euFunded")}
              onChange={(checked) => {
                setEuFunded(checked);
                saveField({ euFunded: checked });
              }}
            />
            <SettingsToggle
              label={t("GPA coverage")}
              description={t("Is this procurement covered by the GPA agreement?")}
              isChecked={gpaCoverage}
              isSaving={isSaving("gpaCoverage")}
              onChange={(checked) => {
                setGpaCoverage(checked);
                saveField({ gpaCoverage: checked });
              }}
            />
            <FormControl>
              <FormLabel fontSize="sm">{t("Reserved participation")} {isSaving("reservedParticipation") && <Spinner size="xs" />}</FormLabel>
              <Select
                size="sm"
                value={reservedParticipation}
                onChange={(e) => {
                  const value = e.target.value as ReservedParticipationType;
                  setReservedParticipation(value);
                  saveField({ reservedParticipation: value });
                }}
              >
                {RESERVED_PARTICIPATION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">{t("Reserved execution")} {isSaving("reservedExecution") && <Spinner size="xs" />}</FormLabel>
              <Select
                size="sm"
                value={reservedExecution}
                onChange={(e) => {
                  const value = e.target.value as ReservedExecutionType;
                  setReservedExecution(value);
                  saveField({ reservedExecution: value });
                }}
              >
                {RESERVED_EXECUTION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </option>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};
